import {
  ACCOUNT_TRANSACTION_CLASSIFICATIONS,
  ACCOUNT_TRANSACTION_DIRECTIONS,
  ACCOUNT_TRANSACTION_SOURCES,
  currencySchema,
  STATEMENT_FILE_MAX_BYTES,
  STATEMENT_FORMATS,
  STATEMENT_ROW_OUTCOMES,
} from '@btsis/core';
import { oc } from '@orpc/contract';
import { z } from 'zod';
import { commonErrors } from './common/errors.js';
import { sortedListRequest } from './common/list.js';
import { moneySchema } from './common/money.js';
import { paginated, paginationInputSchema } from './common/pagination.js';
import { queryBooleanSchema } from './common/query.js';
import { fileNameSchema, presignedUploadSchema, uploadTokenSchema } from './common/upload.js';

const guarded = {
  UNAUTHENTICATED: commonErrors.UNAUTHENTICATED,
  PERMISSION_DENIED: commonErrors.PERMISSION_DENIED,
  TWO_FACTOR_REQUIRED: commonErrors.TWO_FACTOR_REQUIRED,
  OUT_OF_SCOPE: commonErrors.OUT_OF_SCOPE,
  NOT_FOUND: commonErrors.NOT_FOUND,
  CONFLICT: commonErrors.CONFLICT,
  VALIDATION_FAILED: commonErrors.VALIDATION_FAILED,
};

const isoDaySchema = z.iso.date('a day is a real YYYY-MM-DD date');

export const STATEMENT_IMPORT_STATUSES = [
  'uploaded',
  'processing',
  'preview',
  'committed',
  'failed',
  'discarded',
] as const;
export const statementImportStatusSchema = z.enum(STATEMENT_IMPORT_STATUSES);
export type StatementImportStatus = z.infer<typeof statementImportStatusSchema>;

export const ATTACHMENT_SCAN_STATUSES = ['pending', 'clean', 'infected', 'failed'] as const;

export const statementFormatSchema = z.enum(STATEMENT_FORMATS);

/**
 * One version of how a bank's export is read. `mapping` is
 * `statementMappingSchema` from `packages/core` for the mapped formats and
 * empty for CAMT.053 and MT940; it is checked on save, not here, so a refusal
 * names the field rather than failing as a bare 400.
 */
export const statementProfileSchema = z.object({
  id: z.uuid(),
  bankCode: z.string(),
  name: z.string(),
  version: z.number().int().min(1),
  format: statementFormatSchema,
  mapping: z.record(z.string(), z.unknown()),
  isLatest: z.boolean(),
  createdAt: z.string(),
});

export type StatementProfile = z.infer<typeof statementProfileSchema>;

/** A bank account a statement can be imported into, in the caller's organization scope. */
export const importableAccountSchema = z.object({
  id: z.uuid(),
  label: z.string(),
  currency: currencySchema,
  iban: z.string().nullable(),
  organizationId: z.uuid(),
  organizationName: z.string(),
  /**
   * The bank code of the profile the account's latest import that was not
   * thrown away or failed was read with, so the upload dialog can offer that
   * bank's latest version first. Null for an account nothing was imported into.
   */
  lastProfileBankCode: z.string().nullable(),
});

export type ImportableAccount = z.infer<typeof importableAccountSchema>;

export const statementImportSchema = z.object({
  id: z.uuid(),
  organizationId: z.uuid(),
  organizationName: z.string(),
  organizationAccountId: z.uuid(),
  accountLabel: z.string(),
  accountCurrency: currencySchema,
  profileId: z.uuid(),
  profileName: z.string(),
  profileVersion: z.number().int(),
  format: statementFormatSchema,
  status: statementImportStatusSchema,
  fileName: z.string(),
  fileSize: z.string(),
  fileContentType: z.string(),
  scanStatus: z.enum(ATTACHMENT_SCAN_STATUSES),
  periodStart: isoDaySchema.nullable(),
  periodEnd: isoDaySchema.nullable(),
  rowCount: z.number().int(),
  validCount: z.number().int(),
  duplicateCount: z.number().int(),
  rejectedCount: z.number().int(),
  cancelledAt: z.string().nullable(),
  failureCode: z.string().nullable(),
  uploadedByUserId: z.uuid(),
  uploadedByName: z.string(),
  committedByName: z.string().nullable(),
  committedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type StatementImport = z.infer<typeof statementImportSchema>;

export const statementImportRowSchema = z.object({
  id: z.uuid(),
  rowNumber: z.number().int(),
  outcome: z.enum(STATEMENT_ROW_OUTCOMES),
  bookedOn: isoDaySchema.nullable(),
  valueDate: isoDaySchema.nullable(),
  direction: z.enum(ACCOUNT_TRANSACTION_DIRECTIONS).nullable(),
  amount: moneySchema.nullable(),
  description: z.string().nullable(),
  counterpartyName: z.string().nullable(),
  counterpartyIban: z.string().nullable(),
  externalRef: z.string().nullable(),
  balanceAfterMinor: z.string().nullable(),
  classification: z.enum(ACCOUNT_TRANSACTION_CLASSIFICATIONS).nullable(),
  issues: z.array(z.string()),
  duplicateOfTransactionId: z.uuid().nullable(),
  rawRow: z.record(z.string(), z.unknown()),
});

export type StatementImportRow = z.infer<typeof statementImportRowSchema>;

export const STATEMENT_IMPORT_ROW_VIEWS = [
  'all',
  'valid',
  'issues',
  'duplicates',
  'rejected',
  'balanceGaps',
] as const;

export const STATEMENT_IMPORT_SORT_FIELDS = ['createdAt', 'periodStart'] as const;

export const requestStatementUploadInputSchema = z.object({
  organizationAccountId: z.uuid(),
  fileName: fileNameSchema,
  fileSize: z.number().int().min(1).max(STATEMENT_FILE_MAX_BYTES),
});

export const statementUploadSchema = presignedUploadSchema;

export type StatementUpload = z.infer<typeof statementUploadSchema>;

export const createStatementImportInputSchema = z.object({
  organizationAccountId: z.uuid(),
  profileId: z.uuid(),
  uploadToken: uploadTokenSchema,
});

export type CreateStatementImportInput = z.infer<typeof createStatementImportInputSchema>;

export const statementImportListInputSchema = sortedListRequest(
  STATEMENT_IMPORT_SORT_FIELDS,
  'createdAt',
  'desc',
)
  .omit({ search: true })
  .extend({
    organizationAccountId: z.uuid().optional(),
    status: statementImportStatusSchema.optional(),
    periodFrom: isoDaySchema.optional(),
    periodTo: isoDaySchema.optional(),
  });

export type StatementImportListInput = z.infer<typeof statementImportListInputSchema>;

/**
 * Statement import — docs/plans/05-finance-reconciliation.md §5.4. Every route
 * is scoped by the **whole organization** the account belongs to
 * (docs/decisions/0045-reconciliation-is-scoped-by-the-whole-organization.md),
 * and refusals arrive as `details.reason` under `CONFLICT` or
 * `VALIDATION_FAILED`.
 */
export const statementRemovalInputSchema = z.object({
  id: z.uuid(),
  reason: z.string().trim().min(1).max(1000),
  unmatchedOnly: z.boolean().default(false),
});
export type StatementRemovalInput = z.infer<typeof statementRemovalInputSchema>;
export const statementRemovalResultSchema = z.object({
  removedCount: z.number().int().nonnegative(),
  protectedCount: z.number().int().nonnegative(),
  completed: z.boolean(),
});
export type StatementRemovalResult = z.infer<typeof statementRemovalResultSchema>;

export const statementImportContract = {
  cancel: oc
    .route({ method: 'POST', path: '/statement-imports/{id}/cancel' })
    .errors(guarded)
    .input(statementRemovalInputSchema)
    .output(statementRemovalResultSchema),
  delete: oc
    .route({ method: 'DELETE', path: '/statement-imports/{id}' })
    .errors(guarded)
    .input(statementRemovalInputSchema)
    .output(statementRemovalResultSchema),
  /** The active bank accounts of every organization the caller's scope wholly holds. */
  accounts: oc
    .route({ method: 'GET', path: '/statement-imports/accounts' })
    .errors(guarded)
    .input(paginationInputSchema)
    .output(paginated(importableAccountSchema)),

  /**
   * A presigned upload for one statement file. Nothing is written: the token
   * carries the object key, the caller, the declared size and an expiry, signed
   * so `create` can trust it without a row.
   */
  requestUpload: oc
    .route({ method: 'POST', path: '/statement-imports/uploads' })
    .errors(guarded)
    .input(requestStatementUploadInputSchema)
    .output(statementUploadSchema),

  /**
   * The intent row. The server reads the uploaded bytes, sniffs what they are,
   * hashes them and moves them to a key only it has written, then commits the
   * import `uploaded` and queues the scan and the processing.
   */
  create: oc
    .route({ method: 'POST', path: '/statement-imports' })
    .errors(guarded)
    .input(createStatementImportInputSchema)
    .output(statementImportSchema),

  list: oc
    .route({ method: 'GET', path: '/statement-imports' })
    .errors(guarded)
    .input(statementImportListInputSchema)
    .output(paginated(statementImportSchema)),

  get: oc
    .route({ method: 'GET', path: '/statement-imports/{id}' })
    .errors(guarded)
    .input(z.object({ id: z.uuid() }))
    .output(statementImportSchema),

  rows: oc
    .route({ method: 'GET', path: '/statement-imports/{id}/rows' })
    .errors(guarded)
    .input(
      paginationInputSchema.extend({
        id: z.uuid(),
        view: z.enum(STATEMENT_IMPORT_ROW_VIEWS).default('all'),
      }),
    )
    .output(paginated(statementImportRowSchema)),

  /** Every valid row becomes a line; a line the account already holds is counted a duplicate. */
  commit: oc
    .route({ method: 'POST', path: '/statement-imports/{id}/commit' })
    .errors(guarded)
    .input(z.object({ id: z.uuid() }))
    .output(statementImportSchema),

  /** Abandons an uncommitted import; post-commit removal has its own explicit actions. */
  discard: oc
    .route({ method: 'POST', path: '/statement-imports/{id}/discard' })
    .errors(guarded)
    .input(z.object({ id: z.uuid() }))
    .output(statementImportSchema),
};

export const createStatementProfileInputSchema = z.object({
  bankCode: z
    .string()
    .trim()
    .regex(/^[a-z0-9][a-z0-9-]{1,39}$/, 'a bank code is lowercase letters, digits and hyphens'),
  name: z.string().trim().min(1).max(120),
  format: statementFormatSchema,
  mapping: z.record(z.string(), z.unknown()),
});

export type CreateStatementProfileInput = z.infer<typeof createStatementProfileInputSchema>;

export const statementProfileContract = {
  list: oc
    .route({ method: 'GET', path: '/statement-profiles' })
    .errors(guarded)
    .input(
      paginationInputSchema.extend({
        bankCode: z.string().max(40).optional(),
        latestOnly: queryBooleanSchema.default(true),
      }),
    )
    .output(paginated(statementProfileSchema)),

  /** Saving a mapping inserts the bank's next version; no version is ever edited. */
  create: oc
    .route({ method: 'POST', path: '/statement-profiles' })
    .errors(guarded)
    .input(createStatementProfileInputSchema)
    .output(statementProfileSchema),
};

export const ALLOCATION_STATES = ['unallocated', 'partial', 'allocated', 'excluded'] as const;
export const allocationStateSchema = z.enum(ALLOCATION_STATES);

/**
 * The allocation filter: a state, or `open` — unallocated or partly allocated
 * and not excluded, which is the exceptions queue of
 * docs/plans/05-finance-reconciliation.md §5.5.
 */
export const ALLOCATION_FILTERS = [...ALLOCATION_STATES, 'open'] as const;

export const ACCOUNT_TRANSACTION_SORT_FIELDS = ['bookedOn', 'amount'] as const;

/**
 * A movement's frozen TRY, USD and EUR equivalents and the bulletin day they
 * were converted at (docs/decisions/0126). Null while no settled bulletin
 * exists for the day, which the fill job completes. The movement's own amount
 * and currency are never in here and never change.
 */
export const movementEquivalentsSchema = z
  .object({
    TRY: moneySchema,
    USD: moneySchema,
    EUR: moneySchema,
    rateDate: isoDaySchema,
  })
  .nullable();

export type MovementEquivalents = z.infer<typeof movementEquivalentsSchema>;

/** The amount a range filter reads: the movement's own, or one of its three equivalents. */
export const MOVEMENT_AMOUNT_BASES = ['original', 'TRY', 'USD', 'EUR'] as const;
export type MovementAmountBasis = (typeof MOVEMENT_AMOUNT_BASES)[number];

const movementAmountBoundSchema = z.string().regex(/^\d{1,18}$/, 'an amount bound is minor units');

/**
 * The amount filter every movement list and matching candidate list shares.
 * `original` reads the movement's own amount and therefore needs the currency
 * it is asked in; an equivalent needs none, which is the point — a euro payment
 * is found by its lira equivalent. Minor units, either end open.
 *
 * `ratePending` is the *Kur bekleniyor* filter: `true` lists only the movements
 * whose equivalents are not frozen yet, `false` only those that are, and
 * absent both. The three equivalents are all null or all set, so one column
 * decides it.
 */
export const movementAmountFilterShape = {
  currency: currencySchema.optional(),
  amountIn: z.enum(MOVEMENT_AMOUNT_BASES).optional(),
  amountFrom: movementAmountBoundSchema.optional(),
  amountTo: movementAmountBoundSchema.optional(),
  ratePending: queryBooleanSchema.optional(),
};

export function refineMovementAmountFilter(
  input: {
    currency?: unknown;
    amountIn?: MovementAmountBasis | undefined;
    amountFrom?: string | undefined;
    amountTo?: string | undefined;
  },
  context: z.RefinementCtx,
): void {
  if (input.amountFrom === undefined && input.amountTo === undefined) return;
  if ((input.amountIn ?? 'original') === 'original' && input.currency === undefined) {
    context.addIssue({
      code: 'custom',
      path: ['currency'],
      message: 'an amount in the original currency is asked in one currency',
    });
  }
  if (
    input.amountFrom !== undefined &&
    input.amountTo !== undefined &&
    BigInt(input.amountFrom) > BigInt(input.amountTo)
  ) {
    context.addIssue({
      code: 'custom',
      path: ['amountTo'],
      message: 'the upper bound is below the lower bound',
    });
  }
}

export const accountTransactionListItemSchema = z.object({
  id: z.uuid(),
  organizationId: z.uuid(),
  organizationAccountId: z.uuid(),
  accountLabel: z.string(),
  institutionId: z.uuid().nullable(),
  institutionName: z.string().nullable(),
  statementImportId: z.uuid().nullable(),
  source: z.enum(ACCOUNT_TRANSACTION_SOURCES),
  direction: z.enum(ACCOUNT_TRANSACTION_DIRECTIONS),
  amount: moneySchema,
  allocatedMinor: z.string(),
  remaining: moneySchema,
  allocationState: allocationStateSchema,
  bookedOn: isoDaySchema,
  valueDate: isoDaySchema.nullable(),
  description: z.string().nullable(),
  counterpartyName: z.string().nullable(),
  counterpartyEmail: z.string().nullable(),
  externalRef: z.string().nullable(),
  classification: z.enum(ACCOUNT_TRANSACTION_CLASSIFICATIONS),
  excludedAt: z.string().nullable(),
  excludedReason: z.string().nullable(),
  equivalents: movementEquivalentsSchema,
});

export type AccountTransactionListItem = z.infer<typeof accountTransactionListItemSchema>;

export const accountTransactionDetailSchema = accountTransactionListItemSchema.extend({
  financialAccess: z.boolean(),
  counterpartyIban: z.string().nullable(),
  balanceAfterMinor: z.string().nullable(),
  rawRow: z.record(z.string(), z.unknown()),
  createdAt: z.string(),
});

export type AccountTransactionDetail = z.infer<typeof accountTransactionDetailSchema>;

export const accountTransactionListInputSchema = sortedListRequest(
  ACCOUNT_TRANSACTION_SORT_FIELDS,
  'bookedOn',
  'desc',
)
  .extend({
    organizationAccountId: z.uuid().optional(),
    statementImportId: z.uuid().optional(),
    bookedFrom: isoDaySchema.optional(),
    bookedTo: isoDaySchema.optional(),
    direction: z.enum(ACCOUNT_TRANSACTION_DIRECTIONS).optional(),
    classification: z.enum(ACCOUNT_TRANSACTION_CLASSIFICATIONS).optional(),
    allocation: z.enum(ALLOCATION_FILTERS).optional(),
    matchedByOtherOrganization: queryBooleanSchema.optional(),
    ...movementAmountFilterShape,
  })
  .superRefine(refineMovementAmountFilter);

export type AccountTransactionListInput = z.infer<typeof accountTransactionListInputSchema>;

export const accountTransactionReadContract = {
  delete: oc
    .route({ method: 'DELETE', path: '/account-transactions/{id}' })
    .errors(guarded)
    .input(z.object({ id: z.uuid(), reason: z.string().trim().min(1).max(1000) }))
    .output(statementRemovalResultSchema),
  list: oc
    .route({ method: 'GET', path: '/account-transactions' })
    .errors(guarded)
    .input(accountTransactionListInputSchema)
    .output(paginated(accountTransactionListItemSchema)),

  get: oc
    .route({ method: 'GET', path: '/account-transactions/{id}' })
    .errors(guarded)
    .input(z.object({ id: z.uuid() }))
    .output(accountTransactionDetailSchema),
};
